<script setup lang="ts">
import { CtIcon } from '@camelot/components'
import { useOfferAdditionalOptions } from '~/composables/offer-additional-options'
import { formatAmountPerDay } from '~/composables/offer-utils'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { useReferralStore } from '~/stores/referral'

const referralStore = useReferralStore()
const metaFlowOffersEnabled = useRuntimeConfig().public.META_OFFERS_ENABLED && referralStore.isMetaFlow

const extrasStore = useExtrasStore()

const { option: crossBorderOption, shortDescription: crossBorderDescription } = useOfferAdditionalOptions(`cross_border_${useLocationStore().city.country}`, '.')
const { option: comfortOption, shortDescription: comfortOptionDescription } = useOfferAdditionalOptions('cleaned', '\n', 'refueled')
const { option: additionalDriverOption, shortDescription: additionalDriverOptionDescription } = useOfferAdditionalOptions('additional_driver', '\n')

const crossBorderChosen = computed<boolean>(() => extrasStore.propertyChosen(crossBorderOption.value?.id))

const comfortChosen = computed<boolean>(() => extrasStore.propertyChosen(comfortOption.value?.id))

const comfortPreselected = computed<boolean>(() => metaFlowOffersEnabled)

const additionalDriverChosen = computed<boolean>(() => extrasStore.propertyChosen(additionalDriverOption.value?.id))

const formattedCrossBorderPricePerDay = computed<string | undefined>(() => formatAmountPerDay(
  useTranslations().language.value,
  useOfferStore().selectedOffer,
  crossBorderOption.value?.price,
))

const formattedAdditionalDriverPricePerDay = computed<string | undefined>(() => formatAmountPerDay(
  useTranslations().language.value,
  useOfferStore().selectedOffer,
  additionalDriverOption.value?.price,
))

const formattedComfortOptionPricePerDay = computed<string | undefined>(() => formatAmountPerDay(
  useTranslations().language.value,
  useOfferStore().selectedOffer,
  comfortOption.value?.price,
))
</script>

<template>
  <section class="p-16 pb-32 flex flex-col lg:border-t-8 border-t-cotton xs:border-b-0">
    <h4 class="text-black-100 flex items-center">
      <CtIcon name="star" color="--ct-color-black-100" size="40" class="flex-none mr-8" />
      <span class="text-md">{{ translate('sidebar_options') }}</span>
    </h4>

    <section v-if="crossBorderChosen" class="mt-8 flex justify-between items-center text-black-100">
      <div class="ml-8 basis-8/12">
        <p class="text-black-100">
          {{ crossBorderOption!.name }}
        </p>
        <p class="mt-4 text-xs leading-normal text-black-80">
          {{ crossBorderDescription }}
        </p>
      </div>

      <ul class="text-right flex flex-col">
        <li class="text-black-100 text-sm">
          {{ crossBorderOption?.value ?? 'Cross borders' }}
        </li>
        <li class="text-xs leading-normal mt-2 text-black-80">
          {{ formattedCrossBorderPricePerDay }}/{{ translate('amount_per_day') }}
        </li>
      </ul>
    </section>

    <section v-if="additionalDriverChosen" class="mt-16 flex justify-between items-center text-black-100">
      <div class="ml-8 basis-8/12">
        <p class="text-black-100">
          {{ additionalDriverOption!.name }}
        </p>
        <p class="mt-4 text-xs leading-normal text-black-80">
          {{ additionalDriverOptionDescription }}
        </p>
      </div>

      <ul class="text-right flex flex-col">
        <li class="text-black-100 text-sm">
          {{ additionalDriverOption?.value ?? 'Additional Driver' }}
        </li>
        <li class="text-xs leading-normal mt-2 text-black-80">
          {{ formattedAdditionalDriverPricePerDay }}/{{ translate('amount_per_day') }}
        </li>
      </ul>
    </section>

    <section v-if="comfortChosen && !comfortPreselected" class="mt-16 flex justify-between items-center">
      <div class="ml-8 basis-8/12">
        <p class="text-black-100">
          {{ comfortOption!.name }}
        </p>
        <p class="mt-4 text-sm text-black-80">
          {{ comfortOptionDescription }}
        </p>
      </div>

      <ul class="text-right flex flex-col">
        <li class="text-black-100 text-sm">
          {{ comfortOption?.value ?? 'Comfort' }}
        </li>
        <li class="text-xs leading-normal mt-2 text-black-80">
          {{ formattedComfortOptionPricePerDay }}/{{ translate('amount_per_day') }}
        </li>
      </ul>
    </section>

    <section v-if="comfortPreselected" class="mt-16 flex justify-between items-center">
      <div class="ml-8 basis-8/12">
        <p class="text-black-100">
          {{ comfortOption!.name }}
        </p>
        <p class="mt-4 text-sm text-black-80">
          {{ comfortOptionDescription }}
        </p>
      </div>

      <p class="text-right text-mint-80">
        {{ translate('extras_card_included_sub') }}
      </p>
    </section>
  </section>
</template>

<style scoped lang="scss">
.fade-bounce-leave-active {
  animation: bounce 0.3s, scale-opacity 0.3s;
}

.fade-bounce-enter-active {
  animation: bounce 0.1s, scale-opacity 0.1s;
}

.fade-bounce-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-25px);
  }

  60% {
    transform: translateY(-10px);
  }
}

@keyframes scale-opacity {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  60% {
    opacity: 0.3;
    transform: scale(1.1);
  }
}

.clock-enter-active,
.clock-leave-active {
  transition: transform 0.35s, opacity 0.25s;
}

.clock-enter,
.clock-leave-to {
  opacity: 0;
  transform: translateY(40%);
}

.pulse-enter-active,
.pulse-leave-active {
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.pulse-enter,
.pulse-leave-to {
  opacity: 0;
  transform: scale(1.08);
}
</style>
